import { useRouter } from 'next/router'
import React, { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Image from 'next/image'
import { useErrorHandler } from 'react-error-boundary'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { useToast } from '@wartek-id/toast'

import {
  useApiCompetencyMappingQuestionSession,
  useMutateApiCompetencyMappingQuestionAnswer,
} from 'api/competency-mapping/useAPICompetencyMapping'
import PageHeader from 'app/PemetaanKompetensiPage/components/AsesmenRefleksi/PageHeader'
import PeriodGuard from 'app/PemetaanKompetensiPage/components/AsesmenRefleksi/PeriodGuard'
import { useAuth } from 'utils/auth'
import { parseQuery } from 'utils/router'
import QuestionCard from 'app/PemetaanKompetensiPage/components/AsesmenRefleksi/QuestionCard'
import { useMediaQueryHelper } from 'app/PemetaanKompetensiPage/hooks/useMediaQueryHelper'
import CenterDialog from 'app/PemetaanKompetensiPage/components/CenterDialog'
import { NotFoundError } from 'utils/customError'
import { useSETrackEventRefkom } from '../../hooks/useSETrackEventRefkom'
import { capitalizeFirstLetter } from 'utils/text'
import SuccessDialog from 'app/PemetaanKompetensiPage/components/AsesmenRefleksi/SuccessDialog'
import { useApiCompetencyProfile } from 'api/competency-mapping/useAPICompetencyProfile'

import type {
  IUjikomQuestionMeta,
  TQuestionSessionState,
} from 'types/content-platform'
import type { FC } from 'react'

interface FormProps {
  currentDateTimeServer: string
}

const ASSESMENT_TYPE = 'REFLEKSI'

const Form: FC<FormProps> = ({ currentDateTimeServer }) => {
  const { session } = useAuth()
  const router = useRouter()
  const competency = parseQuery<string>(router.query.competency)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [dialogOpened, setDialogOpened] = React.useState<
    'draft' | 'submit' | 'failed' | 'success-variant'
  >(null)

  const toast = useToast()
  const { isDesktop } = useMediaQueryHelper()
  const handleError = useErrorHandler()
  const {
    trackAssessmentItemCheckOpen,
    trackClickRedoAssessmentButton,
    trackClickHelpdeskButton,
    trackAssessmentSuccess,
  } = useSETrackEventRefkom()

  const isRetake = new URLSearchParams(window.location.search).get('retake')

  const submitAnswer = useMutateApiCompetencyMappingQuestionAnswer(
    `${ASSESMENT_TYPE}_${competency?.toUpperCase()}`
  )

  const {
    isSuccess: isSuccessCompetencyProfile,
    data: userCompetencyProfileData,
  } = useApiCompetencyProfile(ASSESMENT_TYPE, {
    enabled: dialogOpened == 'success-variant',
    onSuccess() {
      const audio = document.getElementById('sfx') as HTMLAudioElement
      audio.play()
    },
  })

  const currentCompletedLevel = React.useMemo(() => {
    if (!isSuccessCompetencyProfile) return 0
    return userCompetencyProfileData.data.find(
      (thisData) => thisData.key === competency.toUpperCase()
    ).level
  }, [competency, isSuccessCompetencyProfile, userCompetencyProfileData])

  const REFLEKSI_KOMPETENSI_MAIN_ROUTE = '/refleksi-kompetensi'
  const ASESMEN_REFLEKSI_MAIN_ROUTE = `${REFLEKSI_KOMPETENSI_MAIN_ROUTE}/asesmen-refleksi`

  useEffect(() => {
    if (!!isRetake && !dialogOpened) {
      router.replace(ASESMEN_REFLEKSI_MAIN_ROUTE)
    }
  }, [isRetake, router, dialogOpened, ASESMEN_REFLEKSI_MAIN_ROUTE])

  const { data, isSuccess, isLoading } = useApiCompetencyMappingQuestionSession(
    `${ASSESMENT_TYPE}_${competency?.toUpperCase()}`,
    session?.user?.id,
    !!session.user.id && !!competency && !isRetake,
    (error) => {
      const errorResponseMessage = `${error.response?.data?.message}`

      switch (errorResponseMessage.toLowerCase()) {
        case 'user confirmation still unchecked':
          // TODO: toast message confirmation profile
          toast({
            message: 'Mohon konfirmasi profil terlebih dahulu',
          })
          router.replace(
            `${ASESMEN_REFLEKSI_MAIN_ROUTE}/data-diri/${competency?.toLowerCase()}`
          )
          return
        case 'submitted answer has been completed':
          toast({
            message: 'Kompetensi sudah selesai dikumpulkan',
          })
          router.replace(ASESMEN_REFLEKSI_MAIN_ROUTE)
          return
        case 'invalid assessment type':
          handleError(new NotFoundError('/', error))
          return

        default:
          handleError(error)
          return
      }
    }
  )

  function getInputName(id): string {
    return `input-radio-${id}`
  }

  const questionMeta: IUjikomQuestionMeta = React.useMemo(() => {
    if (data && isSuccess) {
      return data.metadata
    }
    return {
      perPage: 0,
      totalPages: 0,
      lastPage: 0,
    }
  }, [isSuccess, data])

  const isLastPage = currentPage >= questionMeta.totalPages
  const isFirstPage = currentPage == 1

  React.useEffect(() => {
    if (questionMeta.lastPage) {
      setCurrentPage(questionMeta.lastPage)
    }
  }, [questionMeta])

  const {
    formState: { isValid },
    register,
    handleSubmit,
    getValues,
    reset,
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
  })

  const goToTop = () => {
    setTimeout(
      () =>
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        }),
      200
    )
  }

  const handleSaveDraft = useCallback(
    (questions, action: 'next' | 'draft') => {
      // default state is in progress, draft will always get default state
      let saveState: TQuestionSessionState = 'IN_PROGRESS'

      // next and submit
      if (action === 'next') {
        saveState =
          questions.length === data.data.questions.length && isLastPage
            ? 'COMPLETED'
            : 'IN_PROGRESS'
      }

      if (saveState === 'COMPLETED' && dialogOpened != 'submit') {
        setDialogOpened('submit')
        return
      }

      submitAnswer.mutate(
        {
          questionSessionId: data.data.id,
          state: saveState,
          questions,
        },
        {
          onSuccess: () => {
            if (action === 'draft') {
              toast({
                message: 'Berhasil simpan draf',
              })
              setDialogOpened(null)
              router.push({
                pathname: ASESMEN_REFLEKSI_MAIN_ROUTE,
              })
              return
            }

            if (isLastPage) {
              setDialogOpened(null)
              setDialogOpened('success-variant')

              return
            }

            goToTop()
            setCurrentPage(currentPage + 1)
          },
          onError: (error) => {
            if (dialogOpened) {
              setDialogOpened(null)
            }
            if (
              error?.originalError?.response?.data?.message.toLowerCase() ==
              'answer does not valid'
            ) {
              trackAssessmentItemCheckOpen({ subCompetency: competency })
              setDialogOpened('failed')
              // set retake=true in URL
              router.replace(
                `${ASESMEN_REFLEKSI_MAIN_ROUTE}/${competency}?retake=true`,
                null,
                { shallow: true }
              )
              return
            }
            toast({
              message: isLastPage
                ? 'Gagal kumpulkan jawaban. Coba lagi.'
                : 'Gagal simpan draf. Coba lagi.',
            })
          },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dialogOpened,
      submitAnswer,
      data,
      isLastPage,
      currentPage,
      toast,
      router,
      ASESMEN_REFLEKSI_MAIN_ROUTE,
      competency,
    ]
  )

  const handleSuccessDialog = () => {
    trackAssessmentSuccess({ subCompetency: competency })
    router.replace(`${REFLEKSI_KOMPETENSI_MAIN_ROUTE}?source=assessment_form`)
  }

  React.useEffect(() => {
    if (isSuccess && data) {
      const answers = data.data.questions
        .map((thisQuestion) => {
          return {
            id: thisQuestion.id,
            choice: thisQuestion.choices.find(
              (thisChoices) => thisChoices.isSelected
            ),
          }
        })
        .filter((thisAnswered) => !!thisAnswered.choice)
        .map((thisAnswer) => {
          return [getInputName(thisAnswer.id), thisAnswer.choice.id]
        })

      reset(Object.fromEntries(answers))
    }
  }, [isSuccess, data, reset])

  const onError = () => {
    toast({
      message: 'Semua jawaban wajib diisi',
    })
  }

  const onSubmitNext = useCallback(
    (formData) => {
      const questions = Object.keys(formData)
        .filter((questionId) => !!formData[questionId])
        .map((questionId) => {
          return {
            id: questionId.split('-')[2],
            answers: [formData[questionId]],
          }
        })

      handleSaveDraft(questions, 'next')
    },
    [handleSaveDraft]
  )

  const onSubmitDraft = useCallback(() => {
    const values = getValues()
    const questions = Object.keys(values)
      .filter((q) => !!values[q])
      .map((q) => {
        return {
          id: q.split('-')?.[2],
          answers: [values[q]],
        }
      })

    handleSaveDraft(questions, 'draft')
  }, [getValues, handleSaveDraft])

  const RenderNextButton = () => (
    <Button
      isLoading={isLastPage && submitAnswer.isLoading}
      size="lg"
      fullWidth={isFirstPage && !isDesktop}
      disabled={submitAnswer.isLoading}
      className="flex-1"
      aria-disabled={!isValid}
      onClick={() => {
        handleSubmit(onSubmitNext, onError)()
      }}
    >
      Selanjutnya
      <Icon as="i" aria-hidden color="inverse">
        chevron_right
      </Icon>
    </Button>
  )

  const RenderBackButton = () =>
    !isFirstPage && (
      <Button
        color="white"
        size="lg"
        className="flex-1"
        fullWidth={!isDesktop}
        onClick={() => {
          setCurrentPage(currentPage - 1)
          goToTop()
        }}
      >
        <Icon as="i" aria-hidden>
          chevron_left
        </Icon>
        &nbsp;Sebelumnya
      </Button>
    )
  return (
    <PeriodGuard
      fullPageLoading
      currentDateTimeServer={currentDateTimeServer}
      forceLoading={submitAnswer.isLoading && isLastPage}
    >
      <>
        <PageHeader
          title={capitalizeFirstLetter(competency)}
          icon="close"
          subtitle={
            isLoading
              ? 'Mohon tunggu...'
              : `Halaman ${currentPage} dari ${questionMeta.totalPages}`
          }
          withoutShadow
          rightActionLabel="Simpan draf"
          rightActionFunction={() => setDialogOpened('draft')}
          leftActionFunction={() => setDialogOpened('draft')}
        />
        <div className="bg-gray-10">
          <form
            id="questions"
            data-testid="questions"
            className="pt-12 max-w-ujikom m-auto"
            noValidate
          >
            {isSuccess &&
              !!currentPage &&
              data?.data?.questions
                .slice(
                  questionMeta.perPage * (currentPage - 1),
                  questionMeta.perPage * currentPage
                )
                .map((thisQuestion, index) => (
                  <QuestionCard
                    {...thisQuestion}
                    key={`question_${index}`}
                    getInputName={getInputName}
                    register={register}
                  />
                ))}
          </form>
          <div className="bg-white">
            <div className="flex flex-row gap-2 p-4 max-w-ujikom m-auto justify-end">
              <RenderBackButton />
              <RenderNextButton />
            </div>
          </div>
        </div>
        {/* audio */}
        <audio id="sfx">
          <source
            src="/sfx/refleksi-kompetensi/asesmen_sukses.mp3"
            type="audio/mpeg"
          />
        </audio>
        {/* Simpan draf dialog */}
        <CenterDialog
          isLoading={submitAnswer.isLoading}
          open={dialogOpened == 'draft'}
          description="Pilihan yang Anda isi akan disimpan dan Anda bisa lanjutkan nanti. Pastikan draf sudah dikumpulkan sebelum batas waktu, ya."
          handleClose={() => setDialogOpened(null)}
          handleCTASecondary={() => setDialogOpened(null)}
          handleCTA={() => onSubmitDraft()}
          CTATextSecondary="Kembali"
        />
        {/* Kumpulkan dialog */}
        <CenterDialog
          isLoading={submitAnswer.isLoading}
          open={dialogOpened == 'submit'}
          title="Setelah dikumpulkan, jawaban tidak bisa diubah lagi. Tetap kumpulkan?"
          description="Cek ulang dan simpan draf jika perlu."
          CTAText="Kumpulkan"
          IconImage={
            <Image
              src="/illustrations/img-post-mail.png"
              alt=""
              width="120"
              height="120"
            />
          }
          CTATextSecondary="Cek ulang"
          handleClose={() => setDialogOpened(null)}
          handleCTASecondary={() => setDialogOpened(null)}
          handleCTA={() => {
            handleSubmit(onSubmitNext, onError)()
          }}
        />
        {/* Jawaban berhasil dialog variant */}
        <SuccessDialog
          isLoading={submitAnswer.isLoading}
          open={dialogOpened == 'success-variant' && isSuccessCompetencyProfile}
          competency={competency}
          title={`${capitalizeFirstLetter(
            competency
          )} Anda berada di Level ${currentCompletedLevel}`}
          level={currentCompletedLevel}
          handleCTA={() => handleSuccessDialog()}
        />
        {/* Item check dialog */}
        <CenterDialog
          isLoading={submitAnswer.isLoading}
          open={dialogOpened == 'failed'}
          title="Hasil refleksi belum bisa diakses"
          description="Sistem mendeteksi Anda belum membaca dan menjawab soal sesuai dengan keadaan yang sesungguhnya. Mohon maaf, Anda harus ulangi refleksi dari awal. Kunjungi Pusat Bantuan untuk selengkapnya."
          CTAText="Ulangi refleksi dari awal"
          CTATextSecondary="Ke Pusat Bantuan"
          IconImage={
            <Image
              src="/illustrations/img-files-document-failed.png"
              alt=""
              width="120"
              height="120"
            />
          }
          dismissable={false}
          handleCTA={() => {
            trackClickRedoAssessmentButton({ subCompetency: competency })
            router.replace(ASESMEN_REFLEKSI_MAIN_ROUTE)
          }}
          handleCTASecondary={() => {
            trackClickHelpdeskButton({
              subCompetency: competency,
              callback: () => {
                window.open(
                  'https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/21350145358873-Kendala-Mengisi-Refleksi-Kompetensi?focus=true',
                  '_blank'
                )
              },
            })
          }}
        />
      </>
    </PeriodGuard>
  )
}

export default Form
